import {
  closeDrawer,
  onBeforeClose,
  openDrawer,
} from "@/src/components/dialogs";
import { ListConstructor } from "@/src/components/fetchableLists";
import { FolderItemTitle } from "@/src/components/folderItemTitle";
import { IconTab } from "@/src/components/iconTab";
import {
  Appointment,
  Bed,
  Hospitalisation,
  Service,
  StructureUnit,
} from "@/src/data/allTypes";

import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  getItemId,
  getUserId,
  hasPermission,
  isNurse,
  openToast,
  uid,
} from "@/src/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import { openAfterCureActions } from "./cureHook";
import { HospitalisationPage } from "./hospitalisationPage";
import { NurseHospitalisationPage } from "../nurseHospitalisation/nurseHospitalisationPage";

export async function openHospitalisationDrawer({
  title,
  size,
  onImmediateDischarge,
  itemId,
  onConfirm,
}: {
  title?: string | null | undefined;
  itemId: string;
  size?: "small" | "big" | "medium" | "fullPage";
  onImmediateDischarge: () => void;
  onConfirm: (hospitalisation: Hospitalisation) => void;
}) {
  let hospitalisation = await apiFetch("hospitalisations", "loadOne", {
    id: itemId,
  });

  let appointment = await apiFetch("appointments", "loadOne", {
    itemId: getItemId(hospitalisation),
    patientId: getItemId(hospitalisation?.patient),
  });

  if (!hospitalisation) throw new Error("Aucune hospitalisation trouvée.");
  if (!hospitalisation.patient) {
    console.log("hospitalisation", hospitalisation);
    throw new Error("Aucun patient trouvé.");
  }
  let patient = hospitalisation.patient;


  await openDrawer({
    id: "hospitalisationDrawer",
    additionalClasses: size != null ? [size] : [],
    
    title: (
      <FolderItemTitle
        currentPatient={hospitalisation.patient}
        parent={hospitalisation}
        showDischargeButton={true}
      ></FolderItemTitle>
    ),

    content: (
      <HospitalisationPage
        appointment={appointment}
        patient={patient}
        item={hospitalisation}
        onChange={(newValue) => {
          hospitalisation = newValue;
        }}
        onImmediateDischarge={async () => {
          hospitalisation?.status == "finished";
          if (!hospitalisation) return;
          await apiFetch("hospitalisations", "addOne", {
            hospitalisation: {
              id: hospitalisation.id,
              discharge: hospitalisation.discharge,
              status: "finished",
            },
          });
          closeDrawer("hospitalisationDrawer");
          onImmediateDischarge();
        }}
      />
    ),

    onBeforeClose: onBeforeClose,
    onConfirm: async (e) => {
      if (!hospitalisation) throw new Error("Aucune hospitalisation trouvée");
      let res = await apiFetch("hospitalisations", "addOne", {
        hospitalisation: {
          id: hospitalisation.id,
          diagnostics: hospitalisation.diagnostics,
          motives: hospitalisation.motives,
          notes: hospitalisation.notes,
        },
      });

      if (appointment?.protocol) {
        let shouldClose = false;
        await openAfterCureActions({
          appointment,
          item: hospitalisation,
          onConfirm: async () => {
            shouldClose = true;
            await onConfirm(hospitalisation!);
          },
        });

        return shouldClose;
      }

      return await onConfirm(res);
    },
  });
}

export async function openHospitalisationFromAppointment(
  appointment: Appointment,
  onConfirm: (appointment: Appointment) => void
) {
  let patient = appointment.patient;
  let hospitalisation: Hospitalisation | null | undefined = null;
  let hospitalisationId = getItemId(appointment.item);
  if (hospitalisationId) {
    hospitalisation = await apiFetch("hospitalisations", "loadOne", {
      id: hospitalisationId,
    });
  }
  hospitalisation ||= {
    id: hospitalisationId,
    type: "hospitalisation",
    decisionDoctor: getUserId(),
    patient,
    creator: getUserId(),
    confidentiality: getStandardConfidentiality(patient),
    readBy: [],
    motives:
      appointment.initialMotives != null ? appointment.initialMotives : [],
    diagnostics: [],
    status: "inProgress",
  };

  if (appointment.protocol && !hospitalisation?.bed) {
    await openDrawer({
      id: "hospitalisationBedDrawer",
      title: "Choisir un lit",
      content: (
        <HospitalisationBedsChoice
          service={USER.get()?.service!}
          onSelect={async (bed) => {
            if (!hospitalisation) return;
            hospitalisation.bed = bed;
            closeDrawer("hospitalisationBedDrawer");
          }}
        ></HospitalisationBedsChoice>
      ),
    });
    if (!hospitalisation?.bed) return;
  }

  if (!appointment.item) {
    let result = await apiFetch("hospitalisations", "addOne", {
      hospitalisation,
    });
    if (!result) return;
    hospitalisation = result;

    await apiFetch("appointments", "addOne", {
      appointment: {
        id: getItemId(appointment),
        item: getItemId(hospitalisation),
      },
    });
  }

  if (!hospitalisation?.id) throw new Error("Aucune hospitalisation trouvée.");
  if (hasPermission("nurseView") || isNurse(USER.get()))
    return await openNurseHospitalisation(hospitalisationId);

  await openHospitalisationDrawer({
    title: appointment?.type?.name || "Hospitalisation",
    itemId: hospitalisation.id,
    onImmediateDischarge: async () => {},
    onConfirm: async (hospitalisation) => {
      if (!hospitalisation) return false;
      if (appointment.protocol) {
        let shouldClose = false;
        await openAfterCureActions({
          appointment,
          item: hospitalisation,
          onConfirm: async () => {
            shouldClose = true;
            await onConfirm(appointment);
          },
        });

        return shouldClose;
      }

      await apiFetch("appointments", "addOne", {
        appointment: {
          ...appointment,
          item: getItemId(hospitalisation),
          status: "finished",
        },
      });

      await onConfirm(appointment);
    },
  });
}

export async function openNurseHospitalisation(hospitalisationId: string) {
  let hospitalisation = await apiFetch("hospitalisations", "loadOne", {
    id: hospitalisationId,
  });

  if (!hospitalisation) throw new Error("Aucune hospitalisation trouvée");

  await openDrawer({
    id: "nurseHospitalisationDrawer",
    additionalClasses: ["big"],
    title: (
      <FolderItemTitle
        currentPatient={hospitalisation.patient!}
        parent={hospitalisation}
        showDischargeButton={false}
      ></FolderItemTitle>
    ),

    content: (
      <NurseHospitalisationPage
        patient={hospitalisation.patient!}
        item={hospitalisation}
      />
    ),
    footer: <div></div>,
    onConfirm: async (e) => {},
  });
}

export function HospitalisationBedsChoice({
  service,
  enableAll,
  onSelect: onChange,
}: {
  service: Service;
  enableAll?: boolean;
  onSelect: (newBed: Bed, hospitalisations: Hospitalisation[]) => void;
}) {
  let units =
    service?.units?.filter((unit) => unit?.type == "hospitalisation") || [];

  const [activeUnit, setActiveUnit] = useState<
    StructureUnit | null | undefined
  >(units?.[0]);
  const [bed, setBed] = useState<Bed | null | undefined>(null);
  let { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["HospitalisationBedsChoice", activeUnit?.id],
    queryFn: () =>
      apiFetch("hospitalisations", "loadMany", {
        patientId: null,
        bedsIds: activeUnit?.beds?.map((bed) => getItemId(bed?.id) || "") || [],
        statuses: ["inProgress"],
      }),
  });

  let beds = activeUnit?.beds;
  let allHospitalisations = data;

  if (!activeUnit)
    return (
      <div className="c1 gap10 center">Veuillez selectionner une unité</div>
    );

  return (
    <div className="c1 gap10">
      <div className="grid gap10">
        {units?.map((unit) => {
          if (!unit) return;
          return (
            <IconTab
              key={`item-${unit.id}`}
              activeTabId={activeUnit?.id || ""}
              id={unit.id || ""}
              name={unit.name}
              onChange={(newId) => {
                setActiveUnit(unit);
              }}
            ></IconTab>
          );
        })}
      </div>
      <ListConstructor
        isLoading={isLoading}
        isError={isError}
        error={error}
        data={beds}
      >
        <div className="grid c3 gap20">
          {beds?.map((bed) => {
            if (!bed) return;
            let hospitalisations =
              allHospitalisations?.filter(
                (hospitalisation) => hospitalisation.bed?.id == bed.id
              ) || [];
            if (!hospitalisations.length || enableAll)
              return (
                <div
                  key={`item-${bed.id}`}
                  className="card center"
                  onClick={async () => {
                    setBed(bed);
                    onChange(bed, hospitalisations);
                  }}
                >
                  <div className="bold">{bed.name}</div>
                  {hospitalisations.length > 0 && (
                    <div className="orangeish p5 br">Occupé</div>
                  )}
                </div>
              );

            return (
              <div
                key={uid()}
                className="card disabled center"
                style={{
                  background: "RGBA(0,0,0,0.1)",
                  boxShadow: "none",
                }}
                onClick={() => {
                  openToast("warning", "lit déjà occupé");
                }}
              >
                <div className="bold">{bed.name}</div>
              </div>
            );
          })}
        </div>
      </ListConstructor>
    </div>
  );
}
